<script>
import { ariaLabel, color, disabled, size } from "@/abc-dsk/props";

import { CLASS_NAME_COMPONENT_BUTTON } from "./common";

// This causes Vetur's templateProps option to parse the props correctly
const props = {
  ariaLabel,
  color,
  disabled,
  size,
  selected: {
    type: Boolean,
    required: true
  },
  tabbable: {
    type: Boolean,
    required: true
  },
  index: {
    type: Number,
    required: true
  }
};

export default {
  name: "TgSelectorButton",
  props,
  emits: ["blur", "focusNext", "focusPrev", "select"],
  computed: {
    blockName() {
      return CLASS_NAME_COMPONENT_BUTTON;
    },

    tabIndex() {
      return this.tabbable ? "0" : "-1";
    },

    classes() {
      return [
        this.blockName,
        `${this.blockName}--${this.size}`,
        this.selected ? `${this.blockName}--selected-${this.color}` : ""
      ];
    }
  },
  methods: {
    onFocus() {
      this.$refs.input?.focus();
    },

    onInputBlur() {
      this.$emit("blur", this.index);
    },

    onArrowKey(e) {
      switch (e.code) {
        case "ArrowLeft":
        case "ArrowUp":
          this.$emit("focusPrev", this.index);
          break;

        case "ArrowRight":
        case "ArrowDown":
          this.$emit("focusNext", this.index);
          break;

        default:
          break;
      }
    },

    onClick() {
      if (this.disabled) {
        return;
      }

      this.$emit("select", this.index);
      this.onFocus();
    }
  }
};
</script>

<template>
  <button
    ref="input"
    :class="classes"
    type="button"
    role="radio"
    :tabindex="tabIndex"
    :aria-label="ariaLabel"
    :aria-checked="selected"
    :disabled="disabled"
    @blur.prevent="onInputBlur()"
    @click.prevent="onClick($event)"
    @keydown.left.right.up.down.prevent="onArrowKey($event)"
  >
    <slot />
  </button>
</template>

<style lang="less" scoped>
@import "@/abc-dsk/theming/index.less";

.tg-selector-button {
  margin-bottom: 0;
  transition: all 100ms ease-in-out;
  border: 1px solid rgb(@theme-colors[@grey][50]);
  border-radius: 4px;
  outline: none;
  background-color: rgb(@theme-colors[@grey][10]);
  font-weight: 500;
  cursor: pointer;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  &:hover:enabled {
    border-color: rgb(@theme-colors[@grey][50]);
    background-color: rgb(@theme-colors[@grey][50]);
  }

  &:focus:enabled {
    border-color: rgb(@theme-colors[@grey][base]);
    box-shadow: 0 0 0 4px rgba(@theme-colors[@grey][base], 0.3);
  }

  &:disabled {
    opacity: 0.75;
    cursor: not-allowed;
  }

  each(@theme-colors, {
    @name: replace(@key, "@", "");

    &--selected-@{name} {
      @base-color: rgb(@value[base]);
      @dark-color: rgb(@value[110]);

      border-color: @base-color;
      background-color: @base-color;
      color: @colors[ @white];

      > * {
        color: @colors[ @white];
      }

      &:hover:enabled {
        border-color: @dark-color;
        background-color: @dark-color;
      }

      &:focus:enabled {
        border-color: @base-color;
        box-shadow: 0 0 0 4px rgba(@value[base], 0.3);
      }
    }
  });

  &--sm {
    padding: 5px 16px;
  }

  &--md {
    padding: 11px 16px;
  }

  &--lg,
  &--xl,
  &--xxl {
    padding: 12px 20px;
  }
}
</style>
